/**
 * Single place that turns Cognito groups into what the UI shows.
 *
 * This is UX gating only: the `cognito:groups` claim is decoded client-side without verification,
 * so it decides which pages, menu items and buttons are rendered. The backend Lambdas enforce access.
 *
 * Rights are derived from the Cognito `cognito:groups` claim:
 *  - `moderators` grants everything;
 *  - every other known group maps to an area + level (`r` = read, `rw` = read/write);
 *  - any other group name is ignored.
 *
 * Routes (`navigation/config/routeTable.tsx`), the left menu (`LeftNavigationBar/navConfig.tsx`)
 * and in-page checks (`useCan`) all express what they need as a `Requirement` and ask `can()`.
 */
export const MODERATOR_GROUP = 'moderators';

export type Area =
    | 'mockup'            // Mockup Admin API (/admin-api/mockup/*)
    | 'mockupHistory'     // read-only subset of `mockup`: mockup list, products, url paths, stats
    | 'aavProduction'     // print files, print jobs, production time, pillow search
    | 'aavShopify'        // everything in `aavProduction` plus AAV orders and form submissions
    | 'stories'           // Story admin API (/admin-api/story/*)
    | 'mediaViewer'       // read-only published stories (Story Viewer)
    | 'orderIntegration'; // Order integration API (order-api.aav-services.com/admin/*)

export type Level = 'r' | 'rw';

export interface Permissions {
    isModerator: boolean;
    areas: Partial<Record<Area, Level>>;
}

export type Requirement =
    | { moderator: true }
    | { area: Area; level?: Level }
    | { anyOf: Requirement[] };

export const GROUP_TO_AREA: Record<string, [Area, Level]> = {
    'mockup-r': ['mockup', 'r'],
    'mockup-rw': ['mockup', 'rw'],
    'mockup-history-r': ['mockupHistory', 'r'],
    'aav-production-r': ['aavProduction', 'r'],
    'aav-production-rw': ['aavProduction', 'rw'],
    'aav-shopify-r': ['aavShopify', 'r'],
    'aav-shopify-rw': ['aavShopify', 'rw'],
    'media-viewer-r': ['mediaViewer', 'r'],
    'stories-r': ['stories', 'r'],
    'stories-rw': ['stories', 'rw'],
    'order-integration-r': ['orderIntegration', 'r'],
    'order-integration-rw': ['orderIntegration', 'rw'],
};

/** Having an area at some level also grants these areas at the same level. */
const AREA_IMPLIES: Partial<Record<Area, Area[]>> = {
    mockup: ['mockupHistory'],
    aavShopify: ['aavProduction'],
};

const LEVEL_RANK: Record<Level, number> = { r: 1, rw: 2 };

export const EMPTY_PERMISSIONS: Permissions = { isModerator: false, areas: {} };

const own = (table: object, key: string) => Object.prototype.hasOwnProperty.call(table, key);

const grant = (areas: Permissions['areas'], area: Area, level: Level) => {
    const current = areas[area];
    if (!current || LEVEL_RANK[level] > LEVEL_RANK[current]) areas[area] = level;
    (AREA_IMPLIES[area] ?? []).forEach((implied) => {
        const impliedCurrent = areas[implied];
        if (!impliedCurrent || LEVEL_RANK[level] > LEVEL_RANK[impliedCurrent]) areas[implied] = level;
    });
};

export const buildPermissions = (groups?: readonly string[] | null): Permissions => {
    const list = Array.isArray(groups) ? groups.filter((group) => typeof group === 'string') : [];
    const areas: Permissions['areas'] = {};
    list.forEach((group) => {
        if (own(GROUP_TO_AREA, group)) grant(areas, GROUP_TO_AREA[group][0], GROUP_TO_AREA[group][1]);
    });
    return { isModerator: list.includes(MODERATOR_GROUP), areas };
};

export const can = (permissions: Permissions | undefined | null, requirement: Requirement): boolean => {
    if (!permissions) return false;
    if (permissions.isModerator) return true;
    if ('moderator' in requirement) return false;
    if ('anyOf' in requirement) return requirement.anyOf.some((r) => can(permissions, r));
    const have = permissions.areas[requirement.area];
    if (!have) return false;
    return LEVEL_RANK[have] >= LEVEL_RANK[requirement.level ?? 'r'];
};

export const hasAnyAccess = (permissions: Permissions | undefined | null): boolean =>
    !!permissions && (permissions.isModerator || Object.keys(permissions.areas).length > 0);

/**
 * Human-readable label for places that still show or log a single "role"
 * (Pillow Search logs, Print File Review). Never used for access decisions.
 */
export const roleLabel = (groups?: readonly string[] | null): string =>
    Array.isArray(groups) && groups.includes(MODERATOR_GROUP) ? 'moderator' : 'user';
